import { useState, useRef } from 'react';
import { useRoute } from '../../context/RouteContext';
import { useAuth } from '../../context/AuthContext';
import { Modal } from '../common/Modal';
import { downloadGPX, parseGPX } from '../../utils/gpx';
import { buildShareURL, copyToClipboard } from '../../utils/share';
import { shareRouteToFirestore, buildFirestoreShareURL } from '../../services/firestoreRoutes';
import { firebaseReady } from '../../services/firebase';
import './RouteActions.css';

const SaveIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
  </svg>
);
const GpxIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const UploadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);
const ShareIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6M14 11v6M9 6V4h6v2"/>
  </svg>
);

export function RouteActions() {
  const { state, dispatch, showToast } = useRoute();
  const { isSubscribed } = useAuth();
  const { current } = state;
  const [saveOpen, setSaveOpen] = useState(false);
  const [routeName, setRouteName] = useState('');
  const [sharing, setSharing] = useState(false);
  const nameInputRef = useRef(null);
  const importInputRef = useRef(null);

  const hasWaypoints = current.waypoints.filter(w => w.lat != null).length > 0;

  const openSave = () => {
    setRouteName(current.name || 'My Route');
    setSaveOpen(true);
  };

  const handleSave = () => {
    const name = routeName.trim() || 'My Route';
    dispatch({ type: 'SET_ROUTE_NAME', payload: name });
    dispatch({ type: 'SAVE_ROUTE' });
    setSaveOpen(false);
    showToast('Route saved!', 'success');
  };

  const handleExportGPX = async () => {
    if (!hasWaypoints) return;
    await downloadGPX({ ...current, elevation: state.elevation }, { rich: isSubscribed });
    showToast(
      isSubscribed ? 'GPX downloaded — elevation included' : 'GPX downloaded · Pro bakes in elevation',
      'success'
    );
  };

  const handleImportClick = () => importInputRef.current?.click();

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const route = parseGPX(text, file.name.replace(/\.gpx$/i, '') || 'Imported Route');
      dispatch({ type: 'LOAD_ROUTE', payload: { id: null, ...route, legs: null } });
      showToast('GPX imported', 'success');
    } catch (err) {
      showToast(err.message || 'Could not import that GPX file', 'error');
    }
  };

  const handleShare = async () => {
    if (!hasWaypoints) return;
    setSharing(true);
    try {
      if (firebaseReady) {
        const id = await shareRouteToFirestore(current);
        await copyToClipboard(buildFirestoreShareURL(id));
        showToast('Share link copied!', 'success');
      } else {
        await copyToClipboard(buildShareURL(current));
        showToast('Share link copied!', 'success');
      }
    } catch {
      try {
        await copyToClipboard(buildShareURL(current));
        showToast('Share link copied!', 'success');
      } catch {
        showToast('Could not copy link', 'error');
      }
    } finally {
      setSharing(false);
    }
  };

  const handleClear = () => {
    if (!hasWaypoints) return;
    dispatch({ type: 'CLEAR_ROUTE' });
  };

  return (
    <>
      <div className="route-actions">
        <button
          className="route-actions__btn route-actions__btn--primary"
          onClick={openSave}
          disabled={!hasWaypoints}
          title="Save route"
        >
          <SaveIcon /> Save
        </button>
        <button
          className="route-actions__btn route-actions__btn--outline route-actions__btn--icon"
          onClick={handleExportGPX}
          disabled={!hasWaypoints}
          title="Export as GPX"
          aria-label="Export as GPX"
        >
          <GpxIcon />
        </button>
        <button
          className="route-actions__btn route-actions__btn--outline route-actions__btn--icon"
          onClick={handleImportClick}
          title="Import a GPX file"
          aria-label="Import a GPX file"
        >
          <UploadIcon />
        </button>
        <input
          ref={importInputRef}
          type="file"
          accept=".gpx"
          style={{ display: 'none' }}
          onChange={handleImportFile}
        />
        <button
          className="route-actions__btn route-actions__btn--outline route-actions__btn--icon"
          onClick={handleShare}
          disabled={!hasWaypoints || sharing}
          title="Copy share link"
          aria-label="Copy share link"
        >
          {sharing ? <span className="route-actions__spinner" /> : <ShareIcon />}
        </button>
        <button
          className="route-actions__btn route-actions__btn--ghost"
          onClick={handleClear}
          disabled={!hasWaypoints}
          title="Clear route"
          aria-label="Clear route"
        >
          <TrashIcon />
        </button>
      </div>

      {saveOpen && (
        <Modal
          title="Save Route"
          onClose={() => setSaveOpen(false)}
          onConfirm={handleSave}
          confirmLabel="Save"
        >
          <div className="route-actions__save-form">
            <label className="route-actions__label" htmlFor="route-name">
              Route name
            </label>
            <input
              id="route-name"
              ref={nameInputRef}
              className="route-actions__input"
              type="text"
              value={routeName}
              onChange={e => setRouteName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
              maxLength={80}
              autoFocus
            />
          </div>
        </Modal>
      )}
    </>
  );
}
